// ============================================================
// EditorCanvas.jsx — live preview + draggable text layers
// ------------------------------------------------------------
// CSS-filtered <img> plus overlay layers that approximate the canvas-only
// pixel ops so users get live feedback while editing:
//   • highlights → screen/multiply blend wash
//   • tilt-shift → backdrop-blur bands over the top/bottom 25%
//   • grain      → a noise <canvas> on top (pointer-events:none)
//   • vignette / fade → gradient + wash overlays
// (sharpen is approximated inside buildPreviewCSS). The real pixel ops still
// bake on export via canvasExport.js, so the preview is an approximation.
//
// Text layers are absolutely-positioned, draggable divs styled to match the
// baked export (canvasExport.drawTextLayers) so on-screen ≈ saved.
// ============================================================

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { buildPreviewCSS } from '../../utils/filterEngine';
import { TEXT_STYLES, textFillColor } from '../../utils/textStyles';

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
// No-background text blocks get a minimum width so left/right alignment has
// room to shift the text. Mirrors canvasExport.MIN_TEXT_BLOCK_FRAC.
const MIN_BLOCK_FRAC = 0.30;

// ── Crop geometry ─────────────────────────────────────────────────────────
// All crop math runs in stage pixels. Because the stage shrink-wraps the
// <img>, stage-px are uniformly proportional to image-px, so a pixel aspect
// ratio (w/h) can be enforced directly in stage space.
const CROP_MIN_PX = 28;          // smallest crop box side, in stage px
const CROP_HANDLES = ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];

// Locked pixel aspect (w/h) for a preset, or null for freeform.
function aspectRatio(key, stageW, stageH) {
  switch (key) {
    case '1:1':  return 1;
    case '4:5':  return 4 / 5;
    case '9:16': return 9 / 16;
    case '16:9': return 16 / 9;
    case 'original': return stageW / stageH;
    default: return null; // 'free'
  }
}

// Largest centred box of pixel-aspect A within the stage, as 0–1 fractions.
function fitAspectBox(A, W, H) {
  let bw = W;
  let bh = W / A;
  if (bh > H) { bh = H; bw = H * A; }
  return { x: (W - bw) / 2 / W, y: (H - bh) / 2 / H, width: bw / W, height: bh / H };
}

// Translate the whole box, clamped inside the stage.
function moveCropBox(s, dx, dy, W, H) {
  const bw = s.width * W;
  const bh = s.height * H;
  const l = Math.max(0, Math.min(s.x * W + dx, W - bw));
  const t = Math.max(0, Math.min(s.y * H + dy, H - bh));
  return { x: l / W, y: t / H, width: s.width, height: s.height };
}

// Resize from a handle. `A` locks the pixel aspect (width-driven, then
// height-driven if it would overflow); null = freeform per edge.
//
// The aspect lock is applied to CORNER handles only. Edge handles always
// resize a single axis (freeform), so top/bottom change height and left/right
// change width regardless of the selected ratio — otherwise a locked ratio
// leaves nothing for an edge handle to do (top/bottom can't move width, so the
// height would just snap back).
function resizeCropBox(mode, s, dx, dy, W, H, A) {
  const hasN = mode.includes('n');
  const hasS = mode.includes('s');
  const hasW = mode.includes('w');
  const hasE = mode.includes('e');
  const isCorner = (hasN || hasS) && (hasW || hasE);

  let l = s.x * W;
  let t = s.y * H;
  let r = (s.x + s.width) * W;
  let b = (s.y + s.height) * H;

  if (hasW) l = Math.max(0, Math.min(s.x * W + dx, r - CROP_MIN_PX));
  if (hasE) r = Math.min(W, Math.max((s.x + s.width) * W + dx, l + CROP_MIN_PX));
  if (hasN) t = Math.max(0, Math.min(s.y * H + dy, b - CROP_MIN_PX));
  if (hasS) b = Math.min(H, Math.max((s.y + s.height) * H + dy, t + CROP_MIN_PX));

  if (A && isCorner) {
    // Pass 1: width drives height, anchored on the edge the user is NOT dragging.
    let wpx = r - l;
    let hpx = wpx / A;
    if (hasN && !hasS)      t = b - hpx;
    else if (hasS && !hasN) b = t + hpx;
    else { const cyc = (t + b) / 2; t = cyc - hpx / 2; b = cyc + hpx / 2; }

    // Pass 2: if that overflowed vertically, drive from height instead.
    if (t < 0 || b > H) {
      hpx = Math.min(hpx, H);
      wpx = hpx * A;
      if (hasW && !hasE)      l = r - wpx;
      else if (hasE && !hasW) r = l + wpx;
      else { const cxc = (l + r) / 2; l = cxc - wpx / 2; r = cxc + wpx / 2; }
      t = Math.max(0, Math.min(t, H - hpx));
      b = t + hpx;
    }
    // Keep the aspect-locked box inside the horizontal bounds too.
    if (l < 0) { r -= l; l = 0; }
    if (r > W) { l -= (r - W); r = W; }
  }

  return { x: l / W, y: t / H, width: (r - l) / W, height: (b - t) / H };
}

export default function EditorCanvas({
  photo,
  settings,
  textLayers = [],
  activeTextLayerId = null,
  interactive = true,
  onSelectLayer,
  onMoveLayer,
  cropMode = false,
  crop = null,
  cropAspect = 'original',
  onCropChange,
}) {
  const stageRef = useRef(null);
  const grainRef = useRef(null);
  const [stage, setStage] = useState({ w: 0, h: 0 });
  const dragRef = useRef(null);
  const cropDragRef = useRef(null);
  const prevAspectRef = useRef(cropAspect);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setStage({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [photo?.url]);

  // Re-render the grain noise canvas when grain or stage size changes.
  const grain = settings.grain || 0;
  useEffect(() => {
    const cv = grainRef.current;
    if (!cv || grain <= 0 || stage.w < 2 || stage.h < 2) return;
    const w = Math.round(stage.w);
    const h = Math.round(stage.h);
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    const img = ctx.createImageData(w, h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = Math.random() * 255;
      d[i] = d[i + 1] = d[i + 2] = v;
      d[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }, [grain, stage.w, stage.h, photo?.url]);

  const onPointerDown = useCallback((e, layer) => {
    e.stopPropagation();
    onSelectLayer?.(layer.id);
    if (!interactive) return;
    const rect = stageRef.current.getBoundingClientRect();
    dragRef.current = {
      id: layer.id, startX: layer.x, startY: layer.y,
      px: e.clientX, py: e.clientY, w: rect.width, h: rect.height,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [interactive, onSelectLayer]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.px) / d.w;
    const dy = (e.clientY - d.py) / d.h;
    onMoveLayer?.(d.id, { x: clamp01(d.startX + dx), y: clamp01(d.startY + dy) });
  }, [onMoveLayer]);

  const onPointerUp = useCallback((e) => {
    if (dragRef.current) {
      try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) {}
      dragRef.current = null;
    }
  }, []);

  // ── Crop: reframe the box when a fixed-ratio preset is picked ───────────
  // 'free' keeps the current box; fixed ratios snap to the largest centred fit.
  useEffect(() => {
    if (!cropMode) return;
    if (prevAspectRef.current === cropAspect) return;
    if (cropAspect === 'free') { prevAspectRef.current = cropAspect; return; }
    if (stage.w < 2 || stage.h < 2) return; // stage not measured yet — retry
    const A = aspectRatio(cropAspect, stage.w, stage.h);
    onCropChange?.(fitAspectBox(A, stage.w, stage.h));
    prevAspectRef.current = cropAspect;
  }, [cropMode, cropAspect, stage.w, stage.h, onCropChange]);

  const onCropPointerDown = useCallback((e, mode) => {
    e.stopPropagation();
    if (!interactive) return;
    const rect = stageRef.current.getBoundingClientRect();
    cropDragRef.current = {
      mode, start: crop, px: e.clientX, py: e.clientY, w: rect.width, h: rect.height,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [interactive, crop]);

  const onCropPointerMove = useCallback((e) => {
    const d = cropDragRef.current;
    if (!d) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    const next = d.mode === 'move'
      ? moveCropBox(d.start, dx, dy, d.w, d.h)
      : resizeCropBox(d.mode, d.start, dx, dy, d.w, d.h, aspectRatio(cropAspect, d.w, d.h));
    onCropChange?.(next);
  }, [cropAspect, onCropChange]);

  const onCropPointerUp = useCallback((e) => {
    if (cropDragRef.current) {
      try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch (_) {}
      cropDragRef.current = null;
    }
  }, []);

  if (!photo) return null;

  const filter = buildPreviewCSS(settings);

  const vig = settings.vignette || 0;
  const vignetteShadow = vig > 0
    ? `inset 0 0 ${40 + vig * 1.6}px ${vig * 0.6}px rgba(0,0,0,${Math.min(0.8, vig / 110)})`
    : 'none';
  const fade = settings.fade || 0;
  const hl = settings.highlights || 0;
  const tilt = settings.tiltShift || 0;
  const tiltBlur = (tilt / 100) * 6; // 0 → 6px

  return (
    <div className="pe-canvas-wrap">
      <div className="pe-canvas-stage" ref={stageRef}>
        <img
          className="pe-canvas-img"
          src={photo.url}
          alt={photo.filename || 'editing'}
          crossOrigin="anonymous"
          style={{ filter }}
          draggable={false}
        />

        {/* Highlights: screen wash to lift, multiply wash to lower. */}
        {hl !== 0 && (
          <div
            className="pe-canvas-overlay"
            style={hl > 0
              ? { background: '#ffffff', mixBlendMode: 'screen', opacity: (hl / 100) * 0.28 }
              : { background: '#000000', mixBlendMode: 'multiply', opacity: (Math.abs(hl) / 100) * 0.28 }}
          />
        )}

        {/* Tilt-shift: blur the top & bottom 25% bands. */}
        {tilt > 0 && (
          <>
            <div
              className="pe-tilt-band"
              style={{ top: 0, height: '25%', backdropFilter: `blur(${tiltBlur}px)`, WebkitBackdropFilter: `blur(${tiltBlur}px)` }}
            />
            <div
              className="pe-tilt-band"
              style={{ bottom: 0, height: '25%', backdropFilter: `blur(${tiltBlur}px)`, WebkitBackdropFilter: `blur(${tiltBlur}px)` }}
            />
          </>
        )}

        {vig > 0 && <div className="pe-canvas-overlay" style={{ boxShadow: vignetteShadow }} />}
        {fade > 0 && (
          <div className="pe-canvas-overlay" style={{ background: '#b9b6ad', opacity: (fade / 100) * 0.22 }} />
        )}

        {/* Grain noise canvas. */}
        {grain > 0 && (
          <canvas
            ref={grainRef}
            className="pe-grain-canvas"
            style={{ opacity: (grain / 100) * 0.45 }}
          />
        )}

        {/* Text layers. */}
        {textLayers.map((layer) => {
          const st = TEXT_STYLES[layer.style] || TEXT_STYLES.classic;
          const fontPx = Math.max(8, (layer.sizeFrac || 0.09) * stage.h);
          const a = (layer.opacity ?? 55) / 100;
          const color = textFillColor(st, layer);

          let background = 'transparent';
          let padding = '0';
          let borderRadius = '0';
          if (st.bg === 'pill') {
            background = `rgba(0,0,0,${a})`;
            padding = '0.30em 0.45em'; borderRadius = '0.32em';
          } else if (st.bg === 'bubble') {
            background = hexToRgba(layer.color, a);
            padding = '0.30em 0.50em'; borderRadius = '0.5em';
          }

          // Text shadow / glow.
          let textShadow = 'none';
          if (st.glow) textShadow = `0 0 ${fontPx * 0.18}px ${layer.color}, 0 0 ${fontPx * 0.4}px ${layer.color}`;
          else if (st.dropShadow) textShadow = `${fontPx * 0.05}px ${fontPx * 0.05}px ${fontPx * 0.18}px rgba(0,0,0,0.9)`;
          else if (st.shadow) textShadow = '0 1px 3px rgba(0,0,0,0.55)';

          // Outline: transparent fill + stroke via -webkit-text-stroke.
          const strokeW = Math.max(1, fontPx * 0.045);

          // No-background blocks get a min width so alignment has room.
          const minWidth = st.bg === 'none' ? `${MIN_BLOCK_FRAC * stage.w}px` : undefined;

          return (
            <div
              key={layer.id}
              className={`pe-textlayer${layer.id === activeTextLayerId ? ' active' : ''}`}
              style={{
                left: `${(layer.x ?? 0.5) * 100}%`,
                top: `${(layer.y ?? 0.5) * 100}%`,
                fontSize: `${fontPx}px`,
                fontFamily: st.fontFamily,
                fontWeight: st.weight,
                fontStyle: st.italic ? 'italic' : 'normal',
                textTransform: st.uppercase ? 'uppercase' : 'none',
                textAlign: layer.align,
                color: st.outline ? 'transparent' : color,
                WebkitTextStroke: st.outline ? `${strokeW}px ${layer.color}` : undefined,
                textShadow,
                background, padding, borderRadius,
                minWidth,
                maxWidth: `${0.9 * stage.w}px`,
                cursor: interactive ? 'move' : 'default',
              }}
              onPointerDown={(e) => onPointerDown(e, layer)}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              {layer.text || ' '}
            </div>
          );
        })}

        {/* Crop overlay — dims outside the box; drag box/handles to reframe. */}
        {cropMode && stage.w > 1 && (() => {
          const c = crop || { x: 0, y: 0, width: 1, height: 1 };
          const L = c.x * stage.w;
          const T = c.y * stage.h;
          const BW = c.width * stage.w;
          const BH = c.height * stage.h;
          return (
            <div className="pe-crop-overlay">
              {/* Four dim panels around the box (pointer-transparent). */}
              <div className="pe-crop-dim" style={{ left: 0, top: 0, width: '100%', height: T }} />
              <div className="pe-crop-dim" style={{ left: 0, top: T, width: L, height: BH }} />
              <div className="pe-crop-dim" style={{ left: L + BW, top: T, right: 0, height: BH }} />
              <div className="pe-crop-dim" style={{ left: 0, top: T + BH, width: '100%', bottom: 0 }} />

              <div
                className="pe-crop-box"
                style={{ left: L, top: T, width: BW, height: BH }}
                onPointerDown={(e) => onCropPointerDown(e, 'move')}
                onPointerMove={onCropPointerMove}
                onPointerUp={onCropPointerUp}
              >
                <div className="pe-crop-grid" />
                {CROP_HANDLES.map((hkey) => (
                  <div
                    key={hkey}
                    className={`pe-crop-h pe-crop-h-${hkey}`}
                    onPointerDown={(e) => onCropPointerDown(e, hkey)}
                    onPointerMove={onCropPointerMove}
                    onPointerUp={onCropPointerUp}
                  />
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function hexToRgba(hex, a) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return `rgba(49,220,186,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}
